import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { http } from "./api";
import { log } from "./logger";

const AuthCtx = createContext(null);

// Detect preferred language from localStorage → browser → fallback (en).
const detectInitialLang = () => {
  try {
    const stored = localStorage.getItem("proxijob_lang");
    if (stored && ["fr", "en", "es", "de"].includes(stored)) return stored;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (["fr", "en", "es", "de"].includes(nav)) return nav;
  } catch (e) {
    // SSR / privacy mode — localStorage inaccessible. Fall through to default.
    log.warn("[auth] lang detect failed:", e?.message || e);
  }
  return "en";
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [lang, setLang] = useState(detectInitialLang);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 on cold load simply means "no session yet" — expected, not an error.
      // 5xx would be worth knowing about, so log at warn level in dev.
      if (e?.response?.status && e.response.status >= 500) {
        log.warn("[auth] /me failed:", e?.response?.status, e?.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    // Bearer JWT storage — see lib/api.js for the Emergent playbook rationale.
    localStorage.setItem("proxijob_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await http.post("/auth/register", payload);
    // Bearer JWT storage — see lib/api.js for the Emergent playbook rationale.
    localStorage.setItem("proxijob_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await http.post("/auth/logout");
    } catch (e) {
      // Server-side logout failing is not fatal — we still drop the local
      // token so the user is logged out client-side either way. Log so we
      // can spot session-store outages in dev.
      log.warn("[auth] server logout failed:", e?.message || e);
    }
    localStorage.removeItem("proxijob_token");
    setUser(null);
  };

  const setLanguage = (l) => { setLang(l); localStorage.setItem("proxijob_lang", l); };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, logout, lang, setLanguage, checkAuth }}>  {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
